import { useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { logout } from '../store/slices/authSlice'
import toast from 'react-hot-toast'
import {
  expireClientSession,
  refreshAccessTokenIfNeeded,
} from '../services/api'

const EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart']
const WARNING_SECONDS = 60 // show warning 60s before logout
const LAST_ACTIVITY_KEY = 'bms_last_activity_at'
const ACTIVITY_WRITE_THROTTLE_MS = 15_000

export function useSessionTimeout(timeoutMinutes: number | null | undefined) {
  const dispatch = useDispatch()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toastIdRef = useRef<string | null>(null)
  const lastRecordedRef = useRef(0)

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (warningRef.current) clearTimeout(warningRef.current)
    if (toastIdRef.current) toast.dismiss(toastIdRef.current)
    toastIdRef.current = null
  }, [])

  const scheduleFrom = useCallback((lastActivityAt: number) => {
    if (!timeoutMinutes || timeoutMinutes <= 0) return
    clearTimers()

    const ms = timeoutMinutes * 60 * 1000
    const remainingMs = Math.max(0, lastActivityAt + ms - Date.now())
    const warnMs = remainingMs - WARNING_SECONDS * 1000

    if (warnMs > 0) {
      warningRef.current = setTimeout(() => {
        toastIdRef.current = toast(
          `You'll be signed out in ${WARNING_SECONDS} seconds due to inactivity.`,
          { duration: WARNING_SECONDS * 1000, icon: '⏱' }
        ) as string
      }, warnMs)
    }

    timerRef.current = setTimeout(() => {
      toast.dismiss()
      dispatch(logout())
      expireClientSession('idle')
    }, remainingMs)
  }, [timeoutMinutes, clearTimers, dispatch])

  const recordActivity = useCallback(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) return
    const now = Date.now()
    const existing = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || now)
    const timeoutMs = timeoutMinutes * 60 * 1000

    // Never let the first click after a long absence revive an idle session.
    if (now - existing >= timeoutMs) {
      dispatch(logout())
      expireClientSession('idle')
      return
    }

    if (now - lastRecordedRef.current < ACTIVITY_WRITE_THROTTLE_MS) return
    lastRecordedRef.current = now
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    scheduleFrom(now)

    // Active users renew only when the short-lived access token is close to
    // expiry. Inactive open tabs do not rotate refresh tokens in the background.
    void refreshAccessTokenIfNeeded().catch(() => {
      // A valid access token may survive a transient network failure. The next
      // API request owns the authoritative retry/logout decision.
    })
  }, [dispatch, scheduleFrom, timeoutMinutes])

  const checkCurrentIdleState = useCallback(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) return
    const now = Date.now()
    const stored = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || now)
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    }
    if (now - stored >= timeoutMinutes * 60 * 1000) {
      dispatch(logout())
      expireClientSession('idle')
      return
    }
    scheduleFrom(stored)
  }, [dispatch, scheduleFrom, timeoutMinutes])

  useEffect(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) return

    checkCurrentIdleState()
    EVENTS.forEach(e => window.addEventListener(e, recordActivity, { passive: true }))
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkCurrentIdleState()
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
        scheduleFrom(Number(event.newValue))
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', checkCurrentIdleState)
    window.addEventListener('storage', handleStorage)

    return () => {
      clearTimers()
      EVENTS.forEach(e => window.removeEventListener(e, recordActivity))
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', checkCurrentIdleState)
      window.removeEventListener('storage', handleStorage)
    }
  }, [
    timeoutMinutes,
    recordActivity,
    checkCurrentIdleState,
    scheduleFrom,
    clearTimers,
  ])
}
